import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, API } from "@/lib/api";
import { rupees } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  MagnifyingGlass, Trash, ShoppingCart, User, Printer, Receipt, ArrowsLeftRight,
} from "@phosphor-icons/react";

async function openPdf(path) {
  const token = localStorage.getItem("og_token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  window.open(URL.createObjectURL(blob), "_blank");
}

export default function POS() {
  const { activeBranch, user } = useAuth();
  const scanRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [cart, setCart] = useState([]); // {item, product, discount_percent}
  const [scan, setScan] = useState("");
  const [results, setResults] = useState([]);
  const [invDiscount, setInvDiscount] = useState(0);
  const [quote, setQuote] = useState(null);
  const [payMode, setPayMode] = useState("cash");
  const [split, setSplit] = useState(false);
  const [split2, setSplit2] = useState({ mode: "card", amount: 0 });
  const [exchangeOn, setExchangeOn] = useState(false);
  const [exchange, setExchange] = useState({ weight: "", purity: "22K", rate: "", deduction_percent: 0 });
  const [approvalId, setApprovalId] = useState("");
  const [schemeCode, setSchemeCode] = useState("");
  const [redeemable, setRedeemable] = useState([]);
  const [lastSale, setLastSale] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => { api.get("/customers").then(({ data }) => setCustomers(data)); }, []);
  useEffect(() => { api.get("/agents").then(({ data }) => setAgents(data.filter((a) => a.status === "Active"))).catch(() => {}); }, []);  useEffect(() => { scanRef.current?.focus(); }, [cart.length]);

  const addItem = useCallback((item, product) => {
    if (cart.find((c) => c.item.id === item.id)) { toast.info("Item already in cart"); return; }
    if (item.status !== "Available") { toast.error(`Item is ${item.status}`); return; }
    setCart((c) => [...c, { item, product, discount_percent: 0 }]);
    setScan(""); setResults([]);
  }, [cart]);

  const handleScan = async (e) => {
    if (e.key !== "Enter" || !scan.trim()) return;
    try {
      const { data } = await api.get(`/inventory/scan/${scan.trim()}?branch_id=${activeBranch}`);
      addItem(data.item, data.product);
    } catch (err) {
      // treat as search
      try {
        const { data } = await api.get(`/inventory?status=Available&search=${scan.trim()}&branch_id=${activeBranch}`);
        setResults(data.slice(0, 8));
        if (data.length === 0) toast.error("No available item found");
      } catch { toast.error(apiError(err)); }
    }
  };

  const searchItems = async (v) => {
    setScan(v);
    if (v.length < 2) { setResults([]); return; }
    const { data } = await api.get(`/inventory?status=Available&search=${v}&branch_id=${activeBranch}`);
    setResults(data.slice(0, 8));
  };

  const removeLine = (id) => setCart((c) => c.filter((x) => x.item.id !== id));
  useEffect(() => {
    setSchemeCode("");
    if (!customerId) { setRedeemable([]); return; }
    api.get(`/customers/${customerId}/redeemable-schemes`).then(({ data }) => setRedeemable(data)).catch(() => setRedeemable([]));
  }, [customerId]);
  const setLineField = (id, field, v) => setCart((c) => c.map((x) => x.item.id === id ? { ...x, [field]: v } : x));

  const buildPayload = useCallback((withPayments) => {
    const payload = {
      branch_id: activeBranch,
      customer_id: customerId,
      lines: cart.map((c) => {
        const ln = { inventory_item_id: c.item.id, discount_percent: 0 };
        if (c.making !== undefined && c.making !== "") ln.making_override = Number(c.making);
        if (c.wastage !== undefined && c.wastage !== "") ln.wastage_override = Number(c.wastage);
        return ln;
      }),
      invoice_discount_percent: Number(invDiscount) || 0,
      payments: [],
    };
    if (schemeCode.trim()) payload.scheme_account_no = schemeCode.trim();
    if (agentId && agentId !== "none") payload.agent_id = agentId;
    if (exchangeOn && exchange.weight && exchange.rate) {
      payload.exchange = { description: "Old Gold", weight: Number(exchange.weight), purity: exchange.purity, rate: Number(exchange.rate), deduction_percent: Number(exchange.deduction_percent) || 0 };
    }
    if (approvalId) payload.discount_request_id = approvalId;
    if (withPayments && quote) {
      if (split) {
        const a2 = Number(split2.amount) || 0;
        payload.payments = [{ mode: payMode, amount: Math.max(quote.grand_total - a2, 0) }, { mode: split2.mode, amount: a2 }];
      } else {
        payload.payments = [{ mode: payMode, amount: quote.grand_total }];
      }
    }
    return payload;
  }, [activeBranch, customerId, cart, invDiscount, exchangeOn, exchange, approvalId, quote, split, split2, payMode, schemeCode, agentId]);

  // recompute quote (works even before a customer is chosen so prices show on item add)
  useEffect(() => {
    if (cart.length === 0) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pos/quote", buildPayload(false));
        setQuote(data);
      } catch (e) { /* ignore transient */ }
    }, 250);
    return () => clearTimeout(t);
  }, [cart, customerId, invDiscount, exchangeOn, exchange, schemeCode, buildPayload]);

  const checkout = async () => {
    if (!customerId) return toast.error("Select a customer first");
    if (cart.length === 0) return toast.error("Cart is empty");
    setChecking(true);
    try {
      const { data } = await api.post("/pos/checkout", buildPayload(true));
      toast.success(`Invoice ${data.invoice_no} created`);
      setLastSale(data);
      if (customerId) api.get(`/customers/${customerId}/redeemable-schemes`).then(({ data: r }) => setRedeemable(r)).catch(() => {});
      setCart([]); setInvDiscount(0); setExchangeOn(false); setExchange({ weight: "", purity: "22K", rate: "", deduction_percent: 0 }); setApprovalId(""); setSchemeCode(""); setAgentId(""); setSplit(false); setQuote(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setChecking(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 h-[calc(100vh-64px)] overflow-hidden">
      {/* Left: scan + cart */}
      <div className="lg:col-span-8 flex flex-col bg-white overflow-hidden">
        <div className="p-4 border-b border-[#E6E2D6]">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
            <Input
              ref={scanRef} value={scan}
              onChange={(e) => searchItems(e.target.value)}
              onKeyDown={handleScan}
              placeholder="Scan barcode or search SKU / name, then Enter…"
              className="pl-10 h-12 text-base bg-[#FDFBF7] border-[#E6E2D6]" data-testid="pos-scan-input"
            />
            {results.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-[#E6E2D6] rounded-md shadow-md max-h-72 overflow-y-auto">
                {results.map((r) => (
                  <button key={r.id} onClick={() => addItem(r, null)} className="w-full text-left px-4 py-2.5 hover:bg-[#F2EBE1] flex justify-between items-center" data-testid={`pos-result-${r.barcode}`}>
                    <span><span className="font-medium text-sm">{r.product_name}</span> <span className="text-xs text-[#8A8A8A] font-mono ml-2">{r.barcode}</span></span>
                    <span className="text-xs text-[#8A6D2F]">{r.purity} · {Number(r.net_weight).toFixed(2)}g</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-[#8A8A8A]">
              <ShoppingCart size={48} weight="duotone" className="text-[#D8CBB0] mb-3" />
              <p className="text-sm">Scan items to start billing</p>
            </div>
          ) : (
            <div className="space-y-2">
              {cart.map((c, idx) => {
                const ql = quote?.lines?.[idx];
                return (
                  <div key={c.item.id} className="flash-gold border border-[#E6E2D6] rounded-md p-3 flex items-center gap-3" data-testid={`cart-line-${c.item.barcode}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-[#1A1A1A] truncate">{c.item.product_name}</div>
                      <div className="text-xs text-[#8A8A8A] font-mono">{c.item.barcode} · {c.item.purity} · {Number(c.item.net_weight).toFixed(3)}g</div>
                      {ql && <div className="text-xs text-[#8A6D2F] mt-0.5 tabular-nums">Metal {rupees(ql.metal_value)} + Making {rupees(ql.making)} {ql.wastage ? `+ Wastage ${rupees(ql.wastage)}` : ""} {ql.stone_charge ? `+ Stone ${rupees(ql.stone_charge)}` : ""}</div>}
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex items-center gap-1">
                          <Label className="text-[10px] text-[#8A8A8A]">Making ₹</Label>
                          <Input type="number" value={c.making ?? ""} placeholder={ql ? String(Math.round(ql.making)) : ""} onChange={(e) => setLineField(c.item.id, "making", e.target.value)} className="w-20 h-7 bg-white border-[#E6E2D6] text-xs" data-testid={`line-making-${c.item.barcode}`} />
                        </div>
                        <div className="flex items-center gap-1">
                          <Label className="text-[10px] text-[#8A8A8A]">Wastage %</Label>
                          <Input type="number" value={c.wastage ?? ""} onChange={(e) => setLineField(c.item.id, "wastage", e.target.value)} className="w-16 h-7 bg-white border-[#E6E2D6] text-xs" data-testid={`line-wastage-${c.item.barcode}`} />
                        </div>
                      </div>
                    </div>
                    <div className="text-right w-28 tabular-nums font-medium text-[#1A1A1A]">{ql ? rupees(ql.taxable) : "—"}</div>
                    <Button size="icon" variant="ghost" onClick={() => removeLine(c.item.id)} className="text-[#9D2A2A]" data-testid={`remove-${c.item.barcode}`}><Trash size={16} /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right: payment pane */}
      <div className="lg:col-span-4 flex flex-col bg-[#F8F6F0] border-l border-[#E6E2D6] overflow-y-auto">
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1"><User size={13} /> Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="pos-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">Invoice Discount %</Label>
            <Input type="number" value={invDiscount} onChange={(e) => setInvDiscount(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="pos-inv-discount" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">Redeem Matured Scheme</Label>
            <Select value={schemeCode || "none"} onValueChange={(v) => setSchemeCode(v === "none" ? "" : v)} disabled={!customerId || redeemable.length === 0}>
              <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="pos-scheme-code"><SelectValue placeholder={!customerId ? "Select customer first" : "No matured scheme"} /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">{redeemable.length ? "Don't redeem" : "No matured scheme"}</SelectItem>
                {redeemable.map((r) => <SelectItem key={r.account_no} value={r.account_no} data-testid={`pos-redeem-${r.account_no}`}>{r.account_no} — {r.scheme_name} · balance {rupees(r.balance)}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerId && redeemable.length > 0 && !schemeCode && <p className="text-[11px] text-[#2D6A4F] mt-1" data-testid="pos-redeem-hint">{redeemable.length} matured scheme(s) available to deduct from this bill</p>}
          </div>

          {/* Exchange */}
          <div className="bg-white border border-[#E6E2D6] rounded-md p-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1"><ArrowsLeftRight size={13} /> Old Gold Exchange</Label>
              <Switch checked={exchangeOn} onCheckedChange={setExchangeOn} data-testid="pos-exchange-toggle" />
            </div>
            {exchangeOn && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div><Label className="text-[10px] text-[#8A8A8A]">Weight (g)</Label><Input type="number" value={exchange.weight} onChange={(e) => setExchange({ ...exchange, weight: e.target.value })} className="mt-1 h-8 bg-white border-[#E6E2D6]" data-testid="ex-weight" /></div>
                <div><Label className="text-[10px] text-[#8A8A8A]">Rate/g</Label><Input type="number" value={exchange.rate} onChange={(e) => setExchange({ ...exchange, rate: e.target.value })} className="mt-1 h-8 bg-white border-[#E6E2D6]" data-testid="ex-rate" /></div>
                <div>
                  <Label className="text-[10px] text-[#8A8A8A]">Purity</Label>
                  <Select value={exchange.purity} onValueChange={(v) => setExchange({ ...exchange, purity: v })}>
                    <SelectTrigger className="mt-1 h-8 bg-white border-[#E6E2D6]" data-testid="ex-purity"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{["24K", "22K", "18K"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px] text-[#8A8A8A]">Deduction %</Label><Input type="number" value={exchange.deduction_percent} onChange={(e) => setExchange({ ...exchange, deduction_percent: e.target.value })} className="mt-1 h-8 bg-white border-[#E6E2D6]" data-testid="ex-deduction" /></div>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="bg-white border border-[#E6E2D6] rounded-md p-4 space-y-1.5 text-sm">
            <Row label="Subtotal" value={quote ? rupees(quote.subtotal) : "—"} />
            <Row label="Discount" value={quote ? "- " + rupees(quote.total_discount) : "—"} />
            <Row label={`CGST + SGST${quote?.igst ? " / IGST" : ""}`} value={quote ? rupees((quote.cgst + quote.sgst + quote.igst)) : "—"} />
            {quote?.exchange_value > 0 && <Row label="Old Gold Credit" value={"- " + rupees(quote.exchange_value)} green />}
            {quote?.scheme_credit > 0 && <Row label={`Scheme Redeemed (${quote.scheme_account_no})`} value={"- " + rupees(quote.scheme_credit)} green />}
            {quote?.scheme_credit > 0 && quote.scheme_balance_left > 0 && <div className="text-[11px] text-[#8A8A8A] text-right" data-testid="pos-scheme-balance-left">Scheme balance kept for a later bill: {rupees(quote.scheme_balance_left)}</div>}
            <div className="border-t border-[#E6E2D6] pt-2 mt-1 flex items-center justify-between">
              <span className="font-medium text-[#1A1A1A]">Grand Total</span>
              <span className="text-3xl font-medium tabular-nums text-[#1A1A1A]" data-testid="pos-grand-total">{quote ? rupees(quote.grand_total) : "—"}</span>
            </div>
            {quote && quote.effective_discount_percent > (user?.max_discount_percent || 0) && (
              <div className="text-xs text-[#9D2A2A] bg-[#FDEDED] rounded px-2 py-1 mt-1">
                Discount {quote.effective_discount_percent}% exceeds your limit ({user?.max_discount_percent}%). Enter an approval ID below.
              </div>
            )}
          </div>

          {/* Payment */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">Payment Mode</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {["cash", "card", "upi"].map((m) => (
                <button key={m} onClick={() => setPayMode(m)} className={`h-10 rounded-md text-sm capitalize border transition-colors ${payMode === m ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white border-[#E6E2D6] text-[#4A4A4A]"}`} data-testid={`pay-${m}`}>{m}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Switch checked={split} onCheckedChange={setSplit} data-testid="pos-split-toggle" />
              <Label className="text-xs text-[#6B6B6B]">Split payment</Label>
            </div>
            {split && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Select value={split2.mode} onValueChange={(v) => setSplit2({ ...split2, mode: v })}>
                  <SelectTrigger className="h-9 bg-white border-[#E6E2D6]" data-testid="split-mode"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{["cash", "card", "upi"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder="2nd amount" value={split2.amount} onChange={(e) => setSplit2({ ...split2, amount: e.target.value })} className="h-9 bg-white border-[#E6E2D6]" data-testid="split-amount" />
              </div>
            )}
          </div>

          {quote && quote.effective_discount_percent > (user?.max_discount_percent || 0) && (
            <div>
              <Label className="text-xs text-[#6B6B6B]">Approval Request ID</Label>
              <Input value={approvalId} onChange={(e) => setApprovalId(e.target.value)} placeholder="Approved discount request id" className="mt-1 bg-white border-[#E6E2D6]" data-testid="pos-approval-id" />
            </div>
          )}

          <Button onClick={checkout} disabled={checking || !quote} className="w-full h-14 text-lg bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="pos-checkout-btn">
            <Receipt size={20} className="mr-2" /> {checking ? "Processing…" : "Complete Sale (F2)"}
          </Button>

          {lastSale && (
            <div className="bg-[#E8F5E9] border border-[#2D6A4F]/20 rounded-md p-3 fade-in" data-testid="pos-last-sale">
              <div className="text-sm font-medium text-[#2D6A4F]">✓ {lastSale.invoice_no} — {rupees(lastSale.grand_total)}</div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => openPdf(`/sales/${lastSale.id}/invoice.pdf`)} className="border-[#E6E2D6] flex-1" data-testid="print-invoice"><Printer size={14} className="mr-1" /> A4 Invoice</Button>
                <Button size="sm" variant="outline" onClick={() => openPdf(`/sales/${lastSale.id}/thermal.pdf`)} className="border-[#E6E2D6] flex-1" data-testid="print-thermal"><Printer size={14} className="mr-1" /> Thermal</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, green }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#8A8A8A]">{label}</span>
      <span className={`tabular-nums ${green ? "text-[#2D6A4F]" : "text-[#4A4A4A]"}`}>{value}</span>
    </div>
  );
}
